import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { WebsiteSettings } from '@/lib/firebase';
import { Calendar } from 'lucide-react';

const Services = () => {
  const [settings, setSettings] = useState<WebsiteSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const docRef = doc(db, 'settings', 'website');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setSettings(docSnap.data() as WebsiteSettings);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-20">
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center">
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50 z-10"></div>
        <img
          src={settings?.clinic.headerImage || '/header-placeholder.jpg'}
          alt="Services Background"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="container mx-auto px-4 relative z-20">
          <div className="max-w-2xl text-white">
            <h1 className="text-5xl font-bold mb-4">خدماتنا</h1>
            <p className="text-xl">
              نقدم مجموعة متكاملة من خدمات طب الأسنان بأحدث التقنيات العالمية
            </p>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[
            {
              title: 'زراعة الأسنان',
              description: 'أحدث تقنيات زراعة الأسنان مع ضمان النتائج المثالية',
              icon: '🦷',
              details: [
                'زراعة الأسنان الفورية',
                'زراعة الأسنان التقليدية',
                'زراعة الأسنان المتقدمة',
                'إعادة تأهيل الفم بالكامل'
              ]
            },
            {
              title: 'تقويم الأسنان',
              description: 'حلول متكاملة لتقويم الأسنان للكبار والصغار',
              icon: '😁',
              details: [
                'تقويم الأسنان الشفاف',
                'تقويم الأسنان التقليدي',
                'تقويم الأسنان للأطفال',
                'تقويم الأسنان للكبار'
              ]
            },
            {
              title: 'تبييض الأسنان',
              description: 'تبييض احترافي بأحدث التقنيات العالمية',
              icon: '✨',
              details: [
                'تبييض الأسنان بالليزر',
                'تبييض الأسنان المنزلي',
                'تبييض الأسنان في العيادة',
                'حلول تبييض الأسنان المتقدمة'
              ]
            },
            {
              title: 'علاج اللثة',
              description: 'علاج متقدم لأمراض اللثة وحلول جذرية',
              icon: '🦷',
              details: [
                'علاج التهاب اللثة',
                'جراحة اللثة',
                'تنظيف اللثة العميق',
                'علاج أمراض اللثة المتقدمة'
              ]
            },
            {
              title: 'حشوات تجميلية',
              description: 'حشوات تجميلية متطورة تحافظ على جمال ابتسامتك',
              icon: '💎',
              details: [
                'حشوات الكومبوزيت',
                'حشوات السيراميك',
                'حشوات الأملغم',
                'حشوات تجميلية متقدمة'
              ]
            },
            {
              title: 'تركيبات الأسنان',
              description: 'تركيبات ثابتة ومتحركة بأعلى جودة',
              icon: '👑',
              details: [
                'تركيبات الأسنان الثابتة',
                'تركيبات الأسنان المتحركة',
                'قشور السيراميك',
                'جسور الأسنان'
              ]
            }
          ].map((service, index) => (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="text-4xl mb-4">{service.icon}</div>
                <h3 className="text-xl font-bold mb-2">{service.title}</h3>
                <p className="text-gray-600 mb-4">{service.description}</p>
                <ul className="space-y-2 mb-6">
                  {service.details.map((detail, idx) => (
                    <li key={idx} className="flex items-center text-gray-600">
                      <span className="w-2 h-2 bg-blue-600 rounded-full ml-2"></span>
                      {detail}
                    </li>
                  ))}
                </ul>
                <Button
                  className="w-full"
                  onClick={() => navigate('/book-appointment')}
                >
                  <Calendar className="w-5 h-5 ml-2" />
                  احجز موعدك الآن
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Services; 